import type {
  EnrichedSignal,
  UserTrackRecord,
  AlphaTweet,
  PerformanceTier,
} from "@/types";

// Use relative URL - Vite proxy handles the CORS issue in dev mode
const API_BASE_URL = "/api/v1/deep42";
const API_KEY = import.meta.env.VITE_CAMBRIAN_API_KEY || "";

// Simple in-memory cache with TTL
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string): T | null {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;

  const isExpired = Date.now() - entry.timestamp > entry.ttl;
  if (isExpired) {
    cache.delete(key);
    return null;
  }

  return entry.data;
}

function setCache<T>(key: string, data: T, ttlMs: number): void {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMs,
  });
}

// Cache TTL constants
const CACHE_TTL = {
  LEADERBOARD: 5 * 60 * 1000,  // 5 minutes for leaderboard
  FEED: 2 * 60 * 1000,          // 2 minutes for feed
  USER_RECORD: 10 * 60 * 1000,  // 10 minutes for individual user records
};

export interface EnrichedSignalsParams {
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
  excludeZeroReturns?: boolean;
  minAlpha?: number;
  minSentiment?: number;
}

// Fetch enriched signals with price outcomes
export async function fetchEnrichedSignals(
  params: EnrichedSignalsParams = {}
): Promise<EnrichedSignal[]> {
  const searchParams = new URLSearchParams();
  searchParams.set("limit", String(params.limit || 5000));
  if (params.offset) searchParams.set("offset", String(params.offset));
  if (params.startDate) searchParams.set("start_date", params.startDate);
  if (params.endDate) searchParams.set("end_date", params.endDate);
  if (params.excludeZeroReturns !== false) {
    searchParams.set("exclude_zero_returns", "true");
  }
  if (params.minAlpha !== undefined) {
    searchParams.set("min_alpha", String(params.minAlpha));
  }
  if (params.minSentiment !== undefined) {
    searchParams.set("min_sentiment", String(params.minSentiment));
  }

  const url = `${API_BASE_URL}/social-data/enriched-signals?${searchParams.toString()}`;

  const response = await fetch(url, {
    headers: { "X-API-KEY": API_KEY },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Calculate performance tier based on 24h accuracy
function getPerformanceTier(
  accuracy24h: number | null,
  signalCount: number
): PerformanceTier {
  if (signalCount < 10 || accuracy24h === null) return "unproven";
  if (accuracy24h >= 65) return "top_performer";
  if (accuracy24h >= 55) return "proven";
  return "unproven";
}

// Calculate user track records from enriched signals
export function calculateTrackRecords(
  signals: EnrichedSignal[]
): UserTrackRecord[] {
  const userMap = new Map<
    string,
    {
      signals: EnrichedSignal[];
      tokens: Set<string>;
    }
  >();

  // Group signals by user
  for (const signal of signals) {
    if (!userMap.has(signal.twitterHandle)) {
      userMap.set(signal.twitterHandle, { signals: [], tokens: new Set() });
    }
    const user = userMap.get(signal.twitterHandle)!;
    user.signals.push(signal);
    user.tokens.add(signal.tokenSymbol);
  }

  // Calculate track records
  const trackRecords: UserTrackRecord[] = [];

  for (const [handle, data] of userMap) {
    const { signals: userSignals, tokens } = data;

    let bullish = 0,
      bearish = 0;
    let with24h = 0,
      with7d = 0,
      with30d = 0;
    let correct24h = 0,
      correct7d = 0,
      correct30d = 0;
    let totalReturn24h = 0,
      totalReturn7d = 0,
      totalReturn30d = 0;
    let bestReturn: number | null = null;
    let worstReturn: number | null = null;

    for (const sig of userSignals) {
      // Count signal types
      if (sig.signalType === "BULLISH") bullish++;
      else bearish++;

      // Direction multiplier for returns
      const direction = sig.signalType === "BULLISH" ? 1 : -1;

      // 24h outcomes
      if (sig.predictionCorrect24h !== null) {
        with24h++;
        if (sig.predictionCorrect24h) correct24h++;
        if (sig.returnPct24h !== null) {
          const adjReturn = direction * sig.returnPct24h;
          totalReturn24h += adjReturn;
          if (bestReturn === null || adjReturn > bestReturn) bestReturn = adjReturn;
          if (worstReturn === null || adjReturn < worstReturn) worstReturn = adjReturn;
        }
      }

      // 7d outcomes
      if (sig.predictionCorrect7d !== null) {
        with7d++;
        if (sig.predictionCorrect7d) correct7d++;
        if (sig.returnPct7d !== null) {
          totalReturn7d += direction * sig.returnPct7d;
        }
      }

      // 30d outcomes
      if (sig.predictionCorrect30d !== null) {
        with30d++;
        if (sig.predictionCorrect30d) correct30d++;
        if (sig.returnPct30d !== null) {
          totalReturn30d += direction * sig.returnPct30d;
        }
      }
    }

    // Calculate accuracy percentages
    const accuracy24h = with24h > 0 ? (correct24h / with24h) * 100 : null;
    const accuracy7d = with7d > 0 ? (correct7d / with7d) * 100 : null;
    const accuracy30d = with30d > 0 ? (correct30d / with30d) * 100 : null;

    // Calculate average returns
    const avgReturn24h = with24h > 0 ? totalReturn24h / with24h : null;
    const avgReturn7d = with7d > 0 ? totalReturn7d / with7d : null;
    const avgReturn30d = with30d > 0 ? totalReturn30d / with30d : null;

    trackRecords.push({
      twitterHandle: handle,
      totalSignals: userSignals.length,
      bullishSignals: bullish,
      bearishSignals: bearish,
      uniqueTokens: tokens.size,
      signalsWith24hOutcome: with24h,
      signalsWith7dOutcome: with7d,
      signalsWith30dOutcome: with30d,
      correct24h,
      correct7d,
      correct30d,
      accuracy24h,
      accuracy7d,
      accuracy30d,
      avgReturn24h,
      avgReturn7d,
      avgReturn30d,
      bestReturn,
      worstReturn,
      performanceTier: getPerformanceTier(accuracy24h, with24h),
    });
  }

  return trackRecords;
}

// Fetch leaderboard - tries server-side aggregation, falls back to client-side
export async function fetchLeaderboard(
  minSignals: number = 5,
  offset: number = 0,
  pageSize: number = 200
): Promise<UserTrackRecord[]> {
  // Note: API doesn't support offset, so we use limit creatively
  // For pagination, we request (offset + pageSize) records and slice client-side
  const requestLimit = offset + pageSize;
  const cacheKey = `leaderboard-${minSignals}-${offset}-${pageSize}`;

  // Check cache first (only cache first page)
  if (offset === 0) {
    const cached = getCached<UserTrackRecord[]>(cacheKey);
    if (cached) {
      return cached;
    }
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 90);

  // Try server-side aggregation with group_by=twitter_handle
  const searchParams = new URLSearchParams();
  searchParams.set("group_by", "twitter_handle");
  searchParams.set("min_unique_tokens", String(minSignals));
  searchParams.set("exclude_zero_returns", "true");
  searchParams.set("start_date", startDate.toISOString().split("T")[0]);
  searchParams.set("end_date", endDate.toISOString().split("T")[0]);
  searchParams.set("limit", String(requestLimit));

  const url = `${API_BASE_URL}/social-data/enriched-signals?${searchParams.toString()}`;

  const response = await fetch(url, {
    headers: { "X-API-KEY": API_KEY },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Check if response is aggregated (has performanceTier) or raw signals (has signalId)
  if (data.length > 0 && data[0].performanceTier !== undefined) {
    // Server-side aggregation worked - slice for pagination
    const sliced = data.slice(offset, offset + pageSize);
    if (offset === 0) {
      setCache(cacheKey, sliced, CACHE_TTL.LEADERBOARD);
    }
    return sliced;
  }

  // Fallback: API doesn't support group_by yet, do client-side aggregation
  console.log("Server-side aggregation not available, using client-side calculation");
  const signals = await fetchEnrichedSignals({
    limit: 10000,
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
    excludeZeroReturns: true,
  });

  const trackRecords = calculateTrackRecords(signals);

  // Sort by accuracy (with minimum signals), then by signal count
  const filtered = trackRecords
    .filter((tr) => tr.signalsWith24hOutcome >= minSignals)
    .sort((a, b) => {
      // First sort by performance tier
      const tierOrder = { top_performer: 0, proven: 1, unproven: 2 };
      const tierDiff = tierOrder[a.performanceTier] - tierOrder[b.performanceTier];
      if (tierDiff !== 0) return tierDiff;

      // Then by accuracy
      const accA = a.accuracy24h ?? 0;
      const accB = b.accuracy24h ?? 0;
      if (accB !== accA) return accB - accA;

      // Then by signal count
      return b.signalsWith24hOutcome - a.signalsWith24hOutcome;
    });

  // Cache with shorter TTL for client-side calculation
  setCache(cacheKey, filtered, CACHE_TTL.LEADERBOARD);
  return filtered;
}

export interface FeedParams {
  limit?: number;
  minAlpha?: number;
}

// Fetch alpha tweets for feed
export async function fetchAlphaFeed(
  params: FeedParams = {}
): Promise<AlphaTweet[]> {
  const searchParams = new URLSearchParams();
  searchParams.set("limit", String(params.limit || 20));
  if (params.minAlpha !== undefined) {
    searchParams.set("min_alpha", String(params.minAlpha));
  }

  const url = `${API_BASE_URL}/social-data/alpha-tweet-detection?${searchParams.toString()}`;

  const response = await fetch(url, {
    headers: { "X-API-KEY": API_KEY },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// Fetch track record for a specific user
export async function fetchUserTrackRecord(
  twitterHandle: string
): Promise<UserTrackRecord | null> {
  const leaderboard = await fetchLeaderboard();
  return (
    leaderboard.find(
      (tr) => tr.twitterHandle.toLowerCase() === twitterHandle.toLowerCase()
    ) || null
  );
}
